/**
 * Address autocomplete, through `gov-api`.
 *
 * The Places key lives on the server — it bills, and a key in a web bundle is
 * there for anyone to read. This module is only the transport; the field itself
 * is `AddressAutocomplete` in `@munifocus/ui`.
 *
 * The session token is the cost control: Google bills autocomplete per session,
 * so every keystroke's suggestions plus the final details call share one token
 * and count once. The component owns a token's lifetime; this just carries it.
 */
import type { AddressDetail, AddressSuggestion } from '@munifocus/ui';
import { api } from './api-client';

/** A session id for one address entry. */
export function newSessionToken(): string {
  const uuid = globalThis.crypto?.randomUUID;
  if (uuid) return uuid.call(globalThis.crypto);
  return `s-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

/** Which provider is answering, so the right attribution is shown. */
export type ActiveProvider = 'osm' | 'google' | null;

/**
 * Ask the server which provider is live.
 *
 * The provider, not a boolean: attribution is neither optional nor
 * interchangeable. Google requires visible credit wherever its predictions
 * appear outside a Google map; OpenStreetMap requires its own under ODbL.
 */
export async function placesProvider(): Promise<ActiveProvider> {
  try {
    const { data } = await api.get<{ active?: ActiveProvider }>('/places/status');
    return data?.active ?? null;
  } catch {
    return null;
  }
}

export async function fetchAddressSuggestions(
  q: string,
  sessionToken: string,
  language: 'en' | 'fr',
): Promise<AddressSuggestion[]> {
  const { data } = await api.get<{ suggestions: AddressSuggestion[] }>('/places/autocomplete', {
    params: { q, sessionToken, language },
  });
  return data?.suggestions ?? [];
}

export async function fetchAddressDetail(
  placeId: string,
  sessionToken: string,
  language: 'en' | 'fr',
): Promise<AddressDetail | null> {
  const { data } = await api.get<{ place: AddressDetail | null }>('/places/details', {
    params: { placeId, sessionToken, language },
  });
  return data?.place ?? null;
}
